import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ShareAnalytics from "@/components/ShareAnalytics";
import { Bitcoin, Percent, Clock, PauseCircle, Loader2, Save, Megaphone, Send, Eye, Landmark } from "lucide-react";
import { toast } from "sonner";

export default function PlatformSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nudgeBusy, setNudgeBusy] = useState("");
  const [nudgePreview, setNudgePreview] = useState(null);
  const [nudgeDays, setNudgeDays] = useState("5");

  useEffect(() => {
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => setSettings(null));
  }, []);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const runNudges = async (dryRun) => {
    setNudgeBusy(dryRun ? "preview" : "send");
    try {
      const { data } = await api.post(`/admin/run-share-nudges?days=${nudgeDays}&dry_run=${dryRun}`);
      setNudgePreview(data);
      if (dryRun) {
        toast.success(`${data.candidates} campaign(s) haven't been shared in ${nudgeDays}+ days.`);
      } else {
        toast.success(`Nudged ${data.candidates} organizer(s)` + (data.emails_sent === 0 ? " (emails logged — connect Resend to deliver)." : `; ${data.emails_sent} email(s) sent.`));
      }
    } catch (err) { toast.error(errMsg(err)); }
    finally { setNudgeBusy(""); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", {
        crypto_enabled: settings.crypto_enabled,
        default_tip_pct: Number(settings.default_tip_pct),
        review_sla_hours: Number(settings.review_sla_hours),
        submissions_paused: settings.submissions_paused,
        platform_fee_pct: Number(settings.platform_fee_pct),
        pro_fee_pct: Number(settings.pro_fee_pct),
      });
      setSettings(data);
      toast.success("Platform settings saved.");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  if (!settings) return <InlineLoader label="Loading platform settings" />;

  return (
    <div className="max-w-3xl" data-testid="platform-settings-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Platform Settings</h1>
        <p className="mt-1 text-sm text-ff-medium">Control platform-wide behavior. Changes apply immediately to all users.</p>
      </div>

      <div className="space-y-4">
        <ToggleRow icon={Bitcoin} title="Accept crypto payments" testid="setting-crypto"
          desc="Show the 'Pay with Crypto' option to donors on one-time donations."
          checked={!!settings.crypto_enabled} onChange={(v) => set("crypto_enabled", v)} />

        <ToggleRow icon={PauseCircle} title="Pause new campaign submissions" testid="setting-pause"
          desc="Temporarily block organizers from submitting new campaigns for review."
          checked={!!settings.submissions_paused} onChange={(v) => set("submissions_paused", v)} danger />

        <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#F3F4F6] text-ff-blue"><Percent className="h-5 w-5" /></span>
            <div>
              <div className="font-heading font-bold text-ff-blue">Default tip suggestion</div>
              <div className="text-sm text-ff-medium">The tip percentage pre-selected for donors at checkout.</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Input type="number" min="0" max="100" data-testid="setting-tip-input" value={settings.default_tip_pct}
              onChange={(e) => set("default_tip_pct", e.target.value)} className="w-28 font-bold" />
            <span className="text-sm font-semibold text-ff-medium">%</span>
          </div>
        </div>

        <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#F3F4F6] text-ff-blue"><Clock className="h-5 w-5" /></span>
            <div>
              <div className="font-heading font-bold text-ff-blue">Review SLA</div>
              <div className="text-sm text-ff-medium">Campaign reviews waiting longer than this are flagged "overdue".</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Input type="number" min="1" max="720" data-testid="setting-sla-input" value={settings.review_sla_hours}
              onChange={(e) => set("review_sla_hours", e.target.value)} className="w-28 font-bold" />
            <span className="text-sm font-semibold text-ff-medium">hours</span>
          </div>
        </div>

        <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#F3F4F6] text-ff-blue"><Landmark className="h-5 w-5" /></span>
            <div>
              <div className="font-heading font-bold text-ff-blue">Platform fees on funds raised</div>
              <div className="text-sm text-ff-medium">Deducted from an organizer's payout. Pro subscribers get the lower rate.</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="fee-standard">Standard fee</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Input id="fee-standard" type="number" min="0" max="50" step="0.5" data-testid="setting-platform-fee-input"
                  value={settings.platform_fee_pct} onChange={(e) => set("platform_fee_pct", e.target.value)} className="w-24 font-bold" />
                <span className="text-sm font-semibold text-ff-medium">%</span>
              </div>
            </div>
            <div>
              <Label htmlFor="fee-pro">Pro fee</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Input id="fee-pro" type="number" min="0" max="50" step="0.5" data-testid="setting-pro-fee-input"
                  value={settings.pro_fee_pct} onChange={(e) => set("pro_fee_pct", e.target.value)} className="w-24 font-bold" />
                <span className="text-sm font-semibold text-ff-medium">%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-settings-button"
          className="bg-ff-red text-white hover:bg-ff-red/90">
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving</> : <><Save className="mr-2 h-4 w-4" /> Save settings</>}
        </Button>
      </div>

      <ShareAnalytics />

      <div className="mt-8 rounded-2xl border border-ff-border bg-white p-6 shadow-sm" data-testid="share-nudge-card">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FEF2F2] text-ff-red"><Megaphone className="h-5 w-5" /></span>
          <div>
            <div className="font-heading font-bold text-ff-blue">Share nudges</div>
            <div className="text-sm text-ff-medium">Automatically reminds organizers to share campaigns that have gone quiet (runs daily). Preview or send manually below.</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-ff-medium">Idle for</span>
            <Select value={nudgeDays} onValueChange={setNudgeDays}>
              <SelectTrigger className="h-9 w-[110px]" data-testid="nudge-days-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3+ days</SelectItem>
                <SelectItem value="5">5+ days</SelectItem>
                <SelectItem value="7">7+ days</SelectItem>
                <SelectItem value="14">14+ days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" disabled={!!nudgeBusy} data-testid="nudge-preview-button"
            onClick={() => runNudges(true)} className="border-ff-border text-ff-blue hover:border-ff-red">
            {nudgeBusy === "preview" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking</> : <><Eye className="mr-2 h-4 w-4" /> Preview candidates</>}
          </Button>
          <Button disabled={!!nudgeBusy} data-testid="nudge-send-button"
            onClick={() => runNudges(false)} className="bg-ff-blue text-white hover:bg-ff-blue/90">
            {nudgeBusy === "send" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending</> : <><Send className="mr-2 h-4 w-4" /> Send nudges now</>}
          </Button>
        </div>
        {nudgePreview && (
          <div className="mt-4 rounded-lg bg-[#F3F4F6] p-4" data-testid="nudge-result">
            <div className="text-sm font-bold text-ff-blue">
              {nudgePreview.candidates} of {nudgePreview.checked} live campaigns idle {nudgePreview.days}+ days
              {!nudgePreview.dry_run && ` · ${nudgePreview.emails_sent} email(s) sent`}
            </div>
            {nudgePreview.nudged.slice(0, 5).map((n) => (
              <div key={n.campaign_id} className="mt-2 flex items-center justify-between gap-2 text-xs text-ff-dark">
                <span className="truncate font-semibold">{n.title}</span>
                <span className="shrink-0 text-ff-medium">idle {n.days_idle}d · ~{n.est_donors_per_share} donors/share</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ToggleRow({ icon: Icon, title, desc, checked, onChange, testid, danger }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-ff-border bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${danger && checked ? "bg-[#FEF2F2] text-ff-red" : "bg-[#F3F4F6] text-ff-blue"}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <div className="font-heading font-bold text-ff-blue">{title}</div>
          <div className="text-sm text-ff-medium">{desc}</div>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
